import { IDatabaseSearchResult } from "../../../components/tables/ttr/genSearchResults"
import { formatDateTimeToDate } from "../../../utils/general/generalUtils"

// Assumed raw input type
interface IRawSearchResult {
    total_results: number
    query_results: number
    data: []
}

// Required output type
export interface IDatabaseSearchResults {
    totalResults: number
    pageSize: number
    data: IDatabaseSearchResult[]
}

export const ttrUIToApiKey: Record<string, string> = {
    databaseId: "DeviceID",
    deviceReference: "DeviceRef",
    status: "TTRComplianceStatus",
    datePublished: "PublishedDate",
    manufacturer: "TTRManufacturer",
    model: "ModelNo",
    category: "TTRDeviceCategory",
    type: "TTRDeviceType",
    capacity: "RegisteredCapacity",
    phases: "TTRDevicePhase",
    hasActiveAlert: "HasActiveAlert",
    isSubscribed: "IsSubscribed",
    isThreadUnread: "IsThreadUnread",
}

export const searchGenDevicesMapper = {
    /**
     * Map `searchGenDevices` into UI data model, confirming validity of data
     * in process
     * @param {unknown} response - axios client response
     * @returns {IDatabaseSearchResults} - axios client response parsed into
     * UI data model format
     */
    map: (response: unknown): IDatabaseSearchResults => {
        // Confirm response.data is array
        const resTypeGuarded = response as IRawSearchResult
        if (!Array.isArray(resTypeGuarded.data)) {
            throw Error()
        }
        // Parse response.data into UI data model
        const resultsFormatted = (
            resTypeGuarded.data as Record<string, string>[]
        ).map(
            (result): IDatabaseSearchResult => ({
                databaseId: result[
                    ttrUIToApiKey.databaseId
                ] as unknown as number,
                deviceReference: result[ttrUIToApiKey.deviceReference],
                status: result[ttrUIToApiKey.status],
                datePublished: formatDateTimeToDate(
                    result[ttrUIToApiKey.datePublished]
                ),
                manufacturer: result[ttrUIToApiKey.manufacturer],
                model: result[ttrUIToApiKey.model],
                category: result[ttrUIToApiKey.category],
                type: result[ttrUIToApiKey.type],
                capacity: result[ttrUIToApiKey.capacity],
                phases: result[ttrUIToApiKey.phases] as unknown as number,
                hasActiveAlert:
                    String(result[ttrUIToApiKey.hasActiveAlert]) === "true",
                isSubscribed:
                    String(result[ttrUIToApiKey.isSubscribed]) === "true",
                isThreadUnread:
                    String(result[ttrUIToApiKey.isThreadUnread]) === "true",
            })
        )
        return {
            totalResults: resTypeGuarded.total_results,
            pageSize: resTypeGuarded.query_results,
            data: resultsFormatted,
        }
    },
    /**
     * Merges output of paginated requests into one response object.
     * @param pageResults - list of api call responses per page.
     * @returns single output object with `data` field flattened into one array
     */
    mergePaginatedData: (
        pageResults: IDatabaseSearchResults[] | undefined
    ): IDatabaseSearchResults | undefined =>
        // Parses output of API calls into final response format.
        pageResults
            ? {
                  totalResults: pageResults[0].totalResults,
                  pageSize: pageResults[0].pageSize,
                  data: pageResults?.reduce(
                      (
                          acc: IDatabaseSearchResult[],
                          pageResult: IDatabaseSearchResults
                      ) => [...acc, ...pageResult.data],
                      []
                  ),
              }
            : undefined,
}
